import React, { useState } from "react";
import t from "../../components/I18N/TranslateHelpers";
import {
	addFileUploadsToFormData,
	getApplicationFormSectionUrl,
	RenderApplicationFormDetailPart,
} from "../../components/UtilsForApplicationForm";
import { ApplicationFormSection } from "../Entity/ApplicationFormSection";
import { RootState, store } from "../../app/store";
import {
	getApplicationFormSectionParts,
	postApplicationFormSection,
} from "../Slice/ApplicationFormSlice";
import { useSelector } from "react-redux";
import { SpinnerOnTop } from "../../components/SpinnerOnTop";
import { useForm } from "react-hook-form";
import { GetAppFormUsersInfo, getApplicantFormDto } from "features/Slice/ApplicantSlice";
import { ApplicationFormSectionPart } from "features/Entity/ApplicationFormSectionPart";
import SubmitButton from "components/Forms/SubmitButton";
import { ActionButton } from "components/Print/ActionButton";
import { redirectTo, tryScrollBackToPosition } from "components/Utils";
import { ApplicationForm, ProgramTypeEnum } from "features/Entity/ApplicationForm";
import ApplicationFormFinalStep from "./Sections/ApplicationFormFinalStep";
import ApplicationFormDefine from "./Sections/ApplicationFormDefine";
import UploadPDFSection from "components/Forms/UploadPDFSection";
import { getEmptyRequiredFieldsSubmit } from "components/UtilsRequiredField";
import { Applicant, UserType } from "features/Entity/Applicant";
import { notifyAppFormUserOfAccount } from "features/Slice/StaffDefineSlice";
import { ConfirmModal } from "components/ConfirmModal";

interface ComponentProps {
	applicationForm: ApplicationForm;
	applicationFormSection: ApplicationFormSection;
	nextSection: ApplicationFormSection | null;
	isReadOnly: boolean;
}

const ApplicationFormSectionDetails: React.FC<ComponentProps> = ({
	applicationForm,
	applicationFormSection,
	nextSection,
	isReadOnly
}: ComponentProps) => {
	const isRequestingSteps = useSelector((state: RootState) => state.applicationFormRdc.isRequestingParts);
	const applicationFormSectionParts = useSelector((state: RootState) => state.applicationFormRdc.applicationFormSectionParts);
	const isRequestingApplicantData = useSelector((state: RootState) => state.applicantRdc.isRequesting);
	const applicantData = useSelector((state: RootState) => state.applicantRdc.applicantFormDto);
	const isPostingForm = useSelector((state: RootState) => state.applicationFormRdc.isRequestPost);
	const isFormPosted = useSelector((state: RootState) => state.applicationFormRdc.isRequestPostSuccess);
	const currentAppForm = useSelector((state: RootState) => state.applicationFormRdc.applicationForm);
	const isRequestingAppForms = useSelector((state: RootState) => state.applicationFormRdc.isRequesting);
	const isRequestingAgreements = useSelector((state: RootState) => state.agreementRdc.isRequestingList);
	const isRequestingLetters = useSelector((state: RootState) => state.letterRdc.isRequesting);
	const isRequestingMediaAlbum = useSelector((state: RootState) => state.mediaAlbumRdc.isRequesting);
	const isRequestingPhysician = useSelector((state: RootState) => state.physicianRdc.isRequesting);
	const isRequestingSchools = useSelector((state: RootState) => state.schoolRdc.isRequesting);
	const isRequestingSurveys = useSelector((state: RootState) => state.surveyRdc.isRequesting);
	const isRequestingTeacher = useSelector((state: RootState) => state.teacherRdc.isRequesting);
	const isRequestingTutorials = useSelector((state: RootState) => state.tutorialRdc.isRequesting);
	const [notifyingUserEmail, setNotifyingUserEmail] = useState<string | null>(null);
	const [goToNextSection, setGoToNextSection] = useState<boolean>(false);
	const userInfo = useSelector((state: RootState) => state.userRdc.userInfo);
	const isSyncingApplicationForm = useSelector((state: RootState) => state.staffDefineRdc.isSyncing);
	const [notifyingAllUsers, setNotifyingAllUsers] = useState(false);

	const isLastSection = getApplicationFormSectionUrl(applicationFormSection).startsWith(t("url_application_finish"));
	const isDefineSection = getApplicationFormSectionUrl(applicationFormSection).startsWith(t("url_application_form_define"));
	const isAgreementSection = getApplicationFormSectionUrl(applicationFormSection).startsWith(t("url_application_agreement"));

	const appFormUsersInfo = useSelector((state: RootState) => state.applicantRdc.appFormUsersInfo)
	const isFirstOrLastSection = isLastSection || isDefineSection;

	const [notificationModalOpen, setNotificationModalOpen] = useState(false);

		const notifyUserOfAccount = (email?: string) => {
		setNotificationModalOpen(false);

		store.dispatch<any>(notifyAppFormUserOfAccount(applicationForm.uid, email ?? undefined))
			.then(() => {
			store.dispatch(GetAppFormUsersInfo(applicationForm.uid));
			})
			.finally(() => {
			setNotifyingUserEmail(null);
			setNotifyingAllUsers(false);
			});
		};


	React.useEffect(() => {
		if (!isFirstOrLastSection) {
			store.dispatch(
				getApplicationFormSectionParts(
					applicationForm.uid,
					applicationFormSection.id,
				),
			);
		}

		store.dispatch(
			getApplicantFormDto(applicationForm.uid, applicationFormSection.id),
		);
		store.dispatch(
			GetAppFormUsersInfo(applicationForm.uid),
		);
	}, []);

	React.useEffect(() => {
		if (nextSection && isFormPosted && goToNextSection) {
			redirectTo(getApplicationFormSectionUrl(nextSection), false, 500);
		}
		else if (isFormPosted && !goToNextSection && nextSection) {
			window.location.reload();
		}
	}, [isFormPosted]);

	React.useEffect(() => {
		tryScrollBackToPosition([isRequestingSteps, isRequestingApplicantData, isRequestingAppForms, isRequestingAgreements, isRequestingLetters, isRequestingMediaAlbum, isRequestingPhysician, isRequestingSchools, isRequestingSurveys, isRequestingTeacher, isRequestingTutorials, isFormPosted, isPostingForm]);
	}, [isRequestingSteps, isRequestingApplicantData, isRequestingAppForms, isRequestingAgreements, isRequestingLetters, isRequestingMediaAlbum, isRequestingPhysician, isRequestingSchools, isRequestingSurveys, isRequestingTeacher, isRequestingTutorials]);

	const {
		register,
		handleSubmit,
		watch,
		unregister,
		control,
		formState: { errors },
		setValue,
		getValues,
	} = useForm({ mode: "onChange" });


	async function onSubmit(formData: any) {
		await addFileUploadsToFormData(formData);
		store.dispatch(
			postApplicationFormSection(
				applicationForm.uid,
				applicationFormSection.id,
				formData,
			),
		);
	}


	const submitButtonRow = () => {
		return (
			<div className="form-group d-flex justify-content-end buttons-submit">
				<div className="mr-3">
					<SubmitButton
						label={t("update")}
						isPostingForm={isPostingForm}
					/>
				</div>
				{nextSection ? (
					<SubmitButton
						label={t("update_my_data_and_next")}
						isPostingForm={isPostingForm}
						extraActionOnClick={() => setGoToNextSection(true)}
					/>
				) : (
					""
				)}
			</div>
		);
	};

	return isRequestingSteps || isRequestingApplicantData || !applicantData ? (
		<SpinnerOnTop />
	) : (
		<>
			<div className="default-according student-accordion" id="accordionap">
				<div className="col-xl-12 xl-80 col-lg-12 col-md-12 box-col-12">
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="col-md-12 footer-button-container">
							<div className="pb-3 d-flex justify-content-end pdf-buttons">
								{
									!isFirstOrLastSection && (
										<button
											className="btn btn-primary mr-3"
											type="button"
											onClick={() => getEmptyRequiredFieldsSubmit()}
										>
											{t('highlight_missing_info')}
										</button>
									)
								}
								{!isFirstOrLastSection && !isAgreementSection ? (
									<>
										<ActionButton
											applicationForm={applicationForm}
											section={applicationFormSection}
										/>
										{!isReadOnly ? submitButtonRow() : ""}
									</>
								) : (!isFirstOrLastSection && !isReadOnly && !isAgreementSection) ?
									submitButtonRow()
									: ""
								}
							</div>
						</div>
						{
							!isFirstOrLastSection ?
								<div>
									{applicationFormSectionParts?.map(
										(part: ApplicationFormSectionPart, index: number) => {
											return (
												<React.Fragment key={index}>
													{RenderApplicationFormDetailPart(
														applicationForm,
														applicationFormSection,
														part,
														register,
														watch,
														unregister,
														errors,
														control,
														applicantData,
														setValue,
														getValues,
														onSubmit,
														isReadOnly,
														isPostingForm
													)}
												</React.Fragment>
											);
										},
									)}
									{applicationFormSection.canReuploadSectionPDF ? <UploadPDFSection isReadOnly={isReadOnly} applicationFormUid={applicationForm.uid} section={applicationFormSection} register={register} /> : ""}
									{!isReadOnly && !isAgreementSection ? submitButtonRow() : ""}
									
								</div>
								:
								isDefineSection ?
									<><ApplicationFormDefine
										applicationForm={applicationForm}
										section={applicationFormSection}
										usRegister={register}
										usGetValues={getValues}
									/>
									
				{
					(applicationForm?.isStaffForAppForm && applicationForm.programType === ProgramTypeEnum.Outbound) ||
						(applicationForm?.isSendingPartnerForAppForm && applicationForm.programType === ProgramTypeEnum.Inbound) ?
						<div className="card user-infos mb-5">
							<div className="card-header student-header pb-0">
								<h4 className="ps-0 d-flex">User info</h4>
							</div>
							<div className="card-body">
								<div className="user-status table-responsive">
									<table className="table table-bordernone table-odd">
										<thead>
											<tr>
												<th scope="col">{t('name')}</th>
												<th scope="col">{t('date_notification_email_sent')}</th>
												<th scope="col">{t('account_activation_date')}</th>
												<th scope="col">{t('actions')}</th>
											</tr>
										</thead>
										<tbody>
											{appFormUsersInfo.length === 0 && (
												<tr>
													<td colSpan={4} className="text-center text-muted">
														{t('no_users_found')}
													</td>
												</tr>
											)}
											{appFormUsersInfo.map((user: Applicant, index) => (
												<tr key={index}>
													<td className="bd-t-none u-s-tb">
														<div className="align-middle image-sm-size">
															<div className="d-inline-block mt-0">
																<div className="media">
																	<div className="media-body"><span>{user.userEmail}</span>
																	</div>
																</div>
															</div>
														</div>
													</td>
													<td>
														{user.notifiedOfActivationOn && (
															<span className="inline-flex-table">
																{new Date(user.notifiedOfActivationOn).toLocaleDateString('fr-BE')}{' '}
																<h6 className="mb-0">
																	{new Date(user.notifiedOfActivationOn).toLocaleTimeString('fr-BE', {
																		hour: '2-digit',
																		minute: '2-digit',
																	})}
																</h6>
															</span>
														)}
													</td>
													<td>
														{user.accountActivatedOn && (
															<span className="inline-flex-table">
																{new Date(user.accountActivatedOn).toLocaleDateString('fr-BE')}{' '}
																<h6 className="mb-0">
																	{new Date(user.accountActivatedOn).toLocaleTimeString('fr-BE', {
																		hour: '2-digit',
																		minute: '2-digit',
																	})}
																</h6>
															</span>
														)}
													</td>

													<td className="text-center">
														{notifyingUserEmail === user.userEmail ? (
															<SpinnerOnTop />
														) : (
															(user.userType === UserType.LEGAL_GUARDIAN || user.userType === UserType.PARTICIPANT) && (
																<button
																	title={t('resend_email')}
																	className="btn btn-light p-1 rounded-circle icon-sent-email"
																	 onClick={() => [setNotifyingUserEmail(user.userEmail), setNotificationModalOpen(true)]}
																>
																	<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
																		viewBox="0 0 24 24" fill="none" stroke="currentColor"
																		strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
																		className="feather feather-send">
																		<line x1="22" y1="2" x2="11" y2="13" />
																		<polygon points="22 2 15 22 11 13 2 9 22 2" />
																	</svg>
																</button>
															)
														)}
													</td>
												</tr>
											))}
										</tbody>
										<tfoot>
											<tr>
												<td colSpan={4} style={{ textAlign: "right" }}>
													{
													notifyingAllUsers ? (
															<SpinnerOnTop />
														) : 
													<button type="button" disabled={isSyncingApplicationForm} className="btn btn-primary" 
														onClick={() => {
															setNotifyingAllUsers(true);
															setNotifyingUserEmail(null);
															setNotificationModalOpen(true);
															}} data-bs-original-title="" title="">
														<i className="icofont icofont-email mr-2"></i> {t("send_activation_pax_lg")}
													</button>
													}
												</td>
											</tr>
										</tfoot>
									</table>
								</div>
								<ConfirmModal
									isOpen={notificationModalOpen}
									onCancel={() => {
										setNotificationModalOpen(false);
										setNotifyingUserEmail(null);
										setNotifyingAllUsers(false)
									}}
									onConfirm={() => notifyUserOfAccount(notifyingUserEmail ?? undefined)} 
									title="confirm_notify_user_title"
									message="confirm_notify_user_text"
									confirmLabel="send"
									cancelLabel="cancel"
									/>

							</div>
						</div>
						: null
				}
									</>
									:
									<ApplicationFormFinalStep applicationForm={applicationForm} section={applicationFormSection} />
						}
					</form>
				</div>

			</div>
		</>
	);
};

export default ApplicationFormSectionDetails;
